import type { FunctionComponent } from "react";
import { Label } from "./ui/label";
import { Settings2, CircleDot, Gamepad2, Glasses, Gauge } from "lucide-react";
import type GearItem from "@/types/generated/GearItem";

interface GearSelectorProps {
  availableGear: GearItem[];
  selectedGearIds: string[];
  onChange: (gearIds: string[]) => void;
  label?: string;
}

const GEAR_TYPE_ICONS: Record<string, React.ReactNode> = {
  wheel_base: <Settings2 className="w-4 h-4" />,
  steering_wheel: <CircleDot className="w-4 h-4" />,
  pedals: <Gauge className="w-4 h-4" />,
  shifter: <Gamepad2 className="w-4 h-4" />,
  vr_headset: <Glasses className="w-4 h-4" />,
  other: <Settings2 className="w-4 h-4" />,
};

const GEAR_TYPE_LABELS: Record<string, string> = {
  wheel_base: "Wheel Base",
  steering_wheel: "Steering Wheel",
  pedals: "Pedals",
  shifter: "Shifter",
  vr_headset: "VR Headset",
  other: "Other",
};

export const GearSelector: FunctionComponent<GearSelectorProps> = ({
  availableGear,
  selectedGearIds,
  onChange,
  label = "Gear Used",
}) => {
  // Group gear by type
  const gearByType = availableGear.reduce((acc, gear) => {
    if (!acc[gear.gear_type]) {
      acc[gear.gear_type] = [];
    }
    acc[gear.gear_type].push(gear);
    return acc;
  }, {} as Record<string, GearItem[]>);

  const handleGearChange = (gearType: string, gearUuid: string) => {
    // Remove any existing gear of this type
    const filtered = selectedGearIds.filter(
      (id) => !availableGear.find((g) => g.uuid === id && g.gear_type === gearType)
    );

    // Add the new selection (or keep filtered if "none" was selected)
    if (gearUuid) {
      onChange([...filtered, gearUuid]);
    } else {
      onChange(filtered);
    }
  };

  const handleAutoSelect = () => {
    const primaryGear = availableGear.filter((g) => g.is_primary);
    onChange(primaryGear.map((g) => g.uuid));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {availableGear.some((g) => g.is_primary) && (
          <button
            type="button"
            onClick={handleAutoSelect}
            className="text-xs text-blue-600 hover:text-blue-700 underline"
          >
            Auto-select primary gear
          </button>
        )}
      </div>

      {Object.keys(gearByType).length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No gear available.{" "}
          <a href="/garage/new" className="text-blue-600 hover:underline">
            Add gear to your garage
          </a>
        </p>
      ) : (
        <div className="space-y-2">
          {Object.entries(gearByType).map(([type, items]) => {
            const selectedGear = items.find((g) => selectedGearIds.includes(g.uuid));

            return (
              <div key={type} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 min-w-[120px]">
                  {GEAR_TYPE_ICONS[type]}
                  <span className="text-sm text-gray-600">
                    {GEAR_TYPE_LABELS[type]}:
                  </span>
                </div>
                <select
                  value={selectedGear?.uuid || ""}
                  onChange={(e) => handleGearChange(type, e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  {items.map((gear) => (
                    <option key={gear.uuid} value={gear.uuid}>
                      {gear.nickname}
                      {gear.is_primary && " (Primary)"}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
